import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AdminNav from "@/components/AdminNav";
import "@/styles/portfolio.css";
import "@/styles/admin.css";

const EMPTY = {
  client_name: "",
  slug: "",
  message: "",
  videos: [],
  thumbnail_b2_key: "",
  expires_at: "",
  expires_preset: "30",
  access_type: "view_download",
  password: "",
  clear_password: false,
  active: true,
};

function slugify(s) {
  return s
    .toLowerCase()
    .replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function fmtSize(bytes) {
  if (!bytes) return "";
  const mb = bytes / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`;
}

function computeExpiryDate(preset) {
  if (preset === "unlimited") return "";
  if (preset === "custom") return null; // caller handles
  const days = parseInt(preset, 10);
  if (!days) return "";
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function AdminDeliveryEditor() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState({ active: false, progress: 0, label: "" });
  const [error, setError] = useState("");
  const [hadPassword, setHadPassword] = useState(false);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/admin/deliveries/${id}`);
        setForm({
          client_name: data.client_name || "",
          slug: data.slug || "",
          message: data.message || "",
          videos: data.videos || [],
          thumbnail_b2_key: data.thumbnail_b2_key || "",
          expires_at: data.expires_at || "",
          expires_preset: data.expires_at ? "custom" : "unlimited",
          access_type: data.access_type || "view_download",
          password: "",
          clear_password: false,
          active: data.active !== false,
        });
        setHadPassword(Boolean(data.has_password));
        setSlugManuallyEdited(true);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onNameChange = (name) => {
    setForm((f) => ({
      ...f,
      client_name: name,
      slug: slugManuallyEdited ? f.slug : slugify(name),
    }));
  };

  const onPresetChange = (preset) => {
    const auto = computeExpiryDate(preset);
    setForm((f) => ({
      ...f,
      expires_preset: preset,
      expires_at: preset === "custom" ? f.expires_at : auto,
    }));
  };

  const uploadVideo = async (file) => {
    setUploading({ active: true, progress: 0, label: file.name });
    setError("");
    try {
      const { data: presigned } = await api.get(
        `/admin/b2/upload-url?filename=${encodeURIComponent(file.name)}&content_type=${encodeURIComponent(file.type || "video/mp4")}&scope=video`
      );
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("PUT", presigned.upload_url);
        xhr.setRequestHeader("Content-Type", file.type || "video/mp4");
        xhr.upload.onprogress = (evt) => {
          if (evt.lengthComputable) {
            setUploading((u) => ({ ...u, progress: Math.round((evt.loaded / evt.total) * 100) }));
          }
        };
        xhr.onload = () =>
          xhr.status >= 200 && xhr.status < 300
            ? resolve()
            : reject(new Error(`B2 HTTP ${xhr.status}`));
        xhr.onerror = () => reject(new Error("Netzwerkfehler beim Upload"));
        xhr.send(file);
      });
      setForm((f) => ({
        ...f,
        videos: [
          ...f.videos,
          {
            b2_key: presigned.b2_key,
            filename: file.name,
            size: file.size,
            content_type: file.type || "video/mp4",
          },
        ],
      }));
    } catch (e) {
      setError(e.message || "Upload fehlgeschlagen.");
    } finally {
      setUploading({ active: false, progress: 0, label: "" });
    }
  };

  const removeVideo = (idx) => {
    setForm((f) => ({ ...f, videos: f.videos.filter((_, i) => i !== idx) }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.videos.length) {
      setError("Bitte mindestens ein Video hochladen.");
      return;
    }
    setSubmitting(true);
    setError("");
    const payload = {
      client_name: form.client_name.trim(),
      slug: form.slug.trim() || slugify(form.client_name),
      message: form.message.trim(),
      videos: form.videos,
      thumbnail_b2_key: form.thumbnail_b2_key,
      expires_at: form.expires_at || null,
      access_type: form.access_type,
      password: form.password,
      active: form.active,
    };
    try {
      if (isEdit) {
        if (form.clear_password) {
          await api.post(`/admin/deliveries/${id}/clear-password`);
        }
        await api.patch(`/admin/deliveries/${id}`, payload);
      } else {
        await api.post("/admin/deliveries", payload);
      }
      navigate("/admin/deliveries", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  if (loading) {
    return (
      <div className="admin-shell">
        <p className="admin-empty">Lädt…</p>
      </div>
    );
  }

  return (
    <div className="admin-shell" data-testid="admin-delivery-editor">
      <header className="admin-header">
        <div>
          <span className="eyebrow">ADMIN · ÜBERGABE</span>
          <h1 className="display admin-h1">
            {isEdit ? "Übergabe bearbeiten" : "Neue Übergabe"}
          </h1>
        </div>
        <button type="button" onClick={handleLogout} className="btn btn--ghost">
          <span>Abmelden</span>
        </button>
      </header>

      <AdminNav active="/admin/deliveries" />

      {error && <div className="admin-error">{error}</div>}

      <form onSubmit={submit} className="admin-form" data-testid="delivery-form">
        <label className="admin-field">
          <span className="admin-label">Projekt- / Kundenname</span>
          <input
            type="text"
            className="admin-input"
            value={form.client_name}
            onChange={(e) => onNameChange(e.target.value)}
            required
            placeholder="z. B. Restaurant Sonne · Juli 2026"
            data-testid="delivery-client-name"
          />
        </label>

        <label className="admin-field">
          <span className="admin-label">
            URL-Slug <span className="admin-hint">— erscheint in der Adresse: /drop/dein-slug</span>
          </span>
          <div className="admin-slug-row">
            <span className="admin-slug-prefix">/drop/</span>
            <input
              type="text"
              className="admin-input"
              value={form.slug}
              onChange={(e) => {
                setSlugManuallyEdited(true);
                setField("slug", slugify(e.target.value));
              }}
              placeholder="restaurant-sonne-juli"
              data-testid="delivery-slug"
            />
          </div>
        </label>

        <div className="admin-field">
          <span className="admin-label">Videos hochladen</span>
          {form.videos.length > 0 && (
            <ul className="video-list">
              {form.videos.map((v, i) => (
                <li key={v.b2_key} className="video-row">
                  <span className="video-row__icon">▶</span>
                  <span className="video-row__name">{v.filename}</span>
                  <span className="video-row__meta">{fmtSize(v.size)}</span>
                  <button
                    type="button"
                    className="admin-link admin-link--danger"
                    onClick={() => removeVideo(i)}
                  >
                    Entfernen
                  </button>
                </li>
              ))}
            </ul>
          )}
          <label className="admin-upload">
            <input
              type="file"
              accept="video/*"
              hidden
              disabled={uploading.active}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadVideo(f);
                e.target.value = "";
              }}
              data-testid="delivery-video-input"
            />
            <span className="admin-upload__plus">+</span>
            <span className="admin-upload__label">
              {uploading.active
                ? `${uploading.label} — ${uploading.progress}%`
                : form.videos.length === 0
                ? "Erstes Video hochladen"
                : "Weiteres Video hinzufügen"}
            </span>
            <span className="admin-upload__hint">
              Original-Qualität · direkt zu Backblaze B2 EU-Server (Amsterdam)
            </span>
          </label>
        </div>

        <label className="admin-field">
          <span className="admin-label">Persönliche Nachricht an den Kunden</span>
          <textarea
            className="admin-input admin-textarea"
            rows={4}
            value={form.message}
            onChange={(e) => setField("message", e.target.value)}
            placeholder="Hey! Hier sind deine fertigen Reels. Viel Freude damit — bei Fragen einfach melden."
            data-testid="delivery-message"
          />
        </label>

        <div className="admin-row-two">
          <label className="admin-field">
            <span className="admin-label">Gültigkeit</span>
            <select
              className="admin-input"
              value={form.expires_preset}
              onChange={(e) => onPresetChange(e.target.value)}
              data-testid="delivery-expires-preset"
            >
              <option value="7">7 Tage</option>
              <option value="14">14 Tage</option>
              <option value="30">30 Tage</option>
              <option value="90">90 Tage</option>
              <option value="unlimited">Unbegrenzt</option>
              <option value="custom">Benutzerdefiniert</option>
            </select>
          </label>
          {form.expires_preset !== "unlimited" && (
            <label className="admin-field">
              <span className="admin-label">Läuft ab am</span>
              <input
                type="date"
                className="admin-input"
                value={form.expires_at || ""}
                onChange={(e) => setField("expires_at", e.target.value)}
                disabled={form.expires_preset !== "custom"}
                data-testid="delivery-expires-date"
              />
            </label>
          )}
        </div>

        <label className="admin-field">
          <span className="admin-label">Zugriffsart</span>
          <select
            className="admin-input"
            value={form.access_type}
            onChange={(e) => setField("access_type", e.target.value)}
            data-testid="delivery-access-type"
          >
            <option value="view">Nur ansehen (kein Download)</option>
            <option value="view_download">Ansehen + Download</option>
          </select>
        </label>

        <div className="admin-field">
          <span className="admin-label">
            Passwortschutz {hadPassword && !form.clear_password && <em className="admin-hint">— aktuell gesetzt (leer lassen zum Behalten)</em>}
          </span>
          <input
            type="text"
            className="admin-input"
            value={form.password}
            onChange={(e) => setField("password", e.target.value)}
            placeholder={hadPassword ? "•••••••• (leer = unverändert)" : "Leer = kein Passwort"}
            data-testid="delivery-password"
          />
          {hadPassword && (
            <label className="admin-toggle" style={{ marginTop: 6 }}>
              <input
                type="checkbox"
                checked={form.clear_password}
                onChange={(e) => setField("clear_password", e.target.checked)}
              />
              <span>Passwort komplett entfernen</span>
            </label>
          )}
        </div>

        <label className="admin-toggle">
          <input
            type="checkbox"
            checked={form.active}
            onChange={(e) => setField("active", e.target.checked)}
          />
          <span>
            <strong>Aktiv</strong> — Deaktivieren sperrt den Link sofort, unabhängig vom Ablaufdatum
          </span>
        </label>

        <div className="admin-form-foot">
          <Link to="/admin/deliveries" className="btn btn--ghost">
            <span>Abbrechen</span>
          </Link>
          <button
            type="submit"
            disabled={submitting || uploading.active}
            className="btn btn--solid"
            data-testid="delivery-save"
          >
            <span>{submitting ? "Speichert…" : isEdit ? "Speichern" : "Übergabe erstellen"}</span>
            <span className="btn__arrow">→</span>
          </button>
        </div>
      </form>
    </div>
  );
}
